import { useEffect } from "react";

import { getTranslationLabelLanguage } from "../helpers/loadTranslationLabels";
import {
  Column,
  TicketingFormCategory,
  getDollarValue,
  formatCurrencyAmount,
  Locales,
  getIsoLocaleFromLocale,
} from "@simplyk/common";
import { UseFormRegister, Control, Path, FieldValues, UseFormSetValue, useFormContext } from "react-hook-form";
import { v4 } from "uuid";

import { TranslatedRichTextField } from "../sharedComponents/TranslatedRichTextField";
import { useTranslationLocales } from "../Context/TranslationLocaleContext";
import { TranslationCard } from "../sharedComponents/TranslationCard";
import { TranslatedTextField } from "../sharedComponents/TranslatedTextField";
import { useTranslationSwapVersion } from "../sharedComponents/TranslationSwapContext";
import { TicketingTranslationsData } from "../translation.types";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { RateFieldOutput } from "@/common/types/trpc";
import { DashboardRichTextField } from "@/dashboard-design-system/RichTextField/DashboardRichTextField";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";

export interface RatesTranslationCardProps<T extends FieldValues> {
  control: Control<TicketingTranslationsData>;
  register: UseFormRegister<T>;
  idFormName: Path<T>;
  titleFormName: `${Column}.ratesFields.${number}.title`;
  descriptionFormName: `${Column}.ratesFields.${number}.description`;
  index: number;
  amount: number;
  defaultValue?: Pick<RateFieldOutput, "id" | "title" | "description">;
  titleErrorMessage?: string;
  descriptionErrorMessage?: string;
  translationLocale: Locales;
  textToTranslate?: Pick<RateFieldOutput, "id" | "title" | "description">;
  formCategory?: TicketingFormCategory;
  isTranslationColumn: boolean;
  setValue: UseFormSetValue<TicketingTranslationsData>;
  isLoading?: boolean;
}

export function RatesTranslationCard<T extends FieldValues>({
  control,
  register,
  idFormName,
  titleFormName,
  descriptionFormName,
  index,
  amount,
  defaultValue,
  titleErrorMessage,
  descriptionErrorMessage,
  translationLocale,
  textToTranslate,
  isTranslationColumn,
  setValue,
  isLoading,
  formCategory = TicketingFormCategory.Event,
}: RatesTranslationCardProps<T>) {
  const { t } = useTranslate();
  const { organizationCurrency } = useCurrentUserContext();
  const { isoLocale } = useLocaleContext();
  const { getValues } = useFormContext<TicketingTranslationsData>();
  const swapVersion = useTranslationSwapVersion();

  const locales = useTranslationLocales();
  const fieldLocale = isTranslationColumn ? translationLocale : locales?.formLocale;
  const rateLabel = rateLabels[formCategory];
  const title =
    defaultValue?.title ||
    textToTranslate?.title ||
    `${t("dashboard", rateLabel.title, { lng: getTranslationLabelLanguage(fieldLocale) })} ${index + 1}`;

  useEffect(() => {
    if (getValues(descriptionFormName) === undefined) {
      setValue(descriptionFormName, defaultValue?.description ?? "");
    }
  }, [defaultValue?.description, descriptionFormName, getValues, setValue]);

  const descriptionDefaultValue =
    swapVersion > 0 ? getValues(descriptionFormName) : getValues(descriptionFormName) || defaultValue?.description;

  return (
    <TranslationCard
      title={`${title} (${formatCurrencyAmount({
        value: getDollarValue(amount),
        locale: fieldLocale ? getIsoLocaleFromLocale(fieldLocale) : isoLocale,
        currency: organizationCurrency,
      })})`}
    >
      <input style={{ display: "none" }} {...register(idFormName)} value={defaultValue?.id || v4()} />
      {isTranslationColumn ? (
        <>
          {isLoading ? (
            <DashboardSkeleton variant="rounded" width="100%" sx={(theme) => ({ height: theme.spacing(22) })} />
          ) : (
            <>
              <TranslatedTextField
                fieldName={titleFormName}
                defaultFieldValue={textToTranslate?.title}
                defaultTranslationFieldValue={defaultValue?.title}
                setValue={setValue}
                translationLocale={translationLocale}
                label={t("dashboard", rateLabel.title, { lng: getTranslationLabelLanguage(fieldLocale) })}
                rules={[isRequired(t("common", "isRequired"))]}
                control={control}
                errorMessage={titleErrorMessage}
                dataTest="ticketing-rate-title"
                skipTranslation={true}
              />
              <TranslatedRichTextField
                key={swapVersion}
                fieldName={descriptionFormName}
                defaultFieldValue={textToTranslate?.description}
                defaultTranslationFieldValue={descriptionDefaultValue}
                setValue={setValue}
                translationLocale={translationLocale}
                label={t("dashboard", rateLabel.description, { lng: getTranslationLabelLanguage(fieldLocale) })}
                control={control}
                errorMessage={descriptionErrorMessage}
                dataTest="ticketing-rate-description"
                skipTranslation={true}
              />
            </>
          )}
        </>
      ) : (
        <>
          <DashboardTextField
            label={t("dashboard", rateLabel.title, { lng: getTranslationLabelLanguage(fieldLocale) })}
            rules={[isRequired(t("common", "isRequired"))]}
            control={control}
            name={titleFormName}
            defaultValue={defaultValue?.title}
            errorMessage={titleErrorMessage}
            data-test="ticketing-rate-title"
          />
          <DashboardRichTextField
            key={swapVersion}
            label={t("dashboard", rateLabel.description, { lng: getTranslationLabelLanguage(fieldLocale) })}
            control={control}
            name={descriptionFormName}
            defaultValue={descriptionDefaultValue || undefined}
            errorMessage={descriptionErrorMessage}
            data-test="ticketing-rate-description"
          />
        </>
      )}
    </TranslationCard>
  );
}

const rateLabels = {
  [TicketingFormCategory.Event]: { title: "translation.ticketTitle", description: "translation.ticketDescription" },
  [TicketingFormCategory.Lottery]: { title: "translation.ticketTitle", description: "translation.ticketDescription" },
  [TicketingFormCategory.Sweepstakes]: {
    title: "translation.entryTitle",
    description: "translation.entryDescription",
  },
  [TicketingFormCategory.Custom]: { title: "translation.ticketTitle", description: "translation.ticketDescription" },
  [TicketingFormCategory.MembershipV2]: {
    title: "translation.membershipTitle",
    description: "translation.membershipDescription",
  },
  [TicketingFormCategory.Shop]: { title: "translation.productTitle", description: "translation.productDescription" },
  [TicketingFormCategory.Auction]: { title: "translation.itemTitle", description: "translation.itemDescription" },
} as const;
